// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

/**
 * WebSocket Context Provider
 *
 * Centralizes WebSocket management to ensure a single connection per session.
 * All components access WebSocket via useWebSocketContext() hook.
 *
 * Message handling is delegated to useMessageHandlers() which reads fresh
 * store state on each message via getState(), avoiding stale closures.
 */

import {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMessageHandlers } from '../hooks/useMessageHandlers';
import { logger } from '../lib/logger';
import { useWebSocket, ConnectionStatus, type ConnectionStatusType } from '../lib/useWebSocket';
import { useGameStore } from '../store/gameStore';
import type { SendMessageFn } from '../types/messages';

interface WebSocketContextValue {
  sendMessage: SendMessageFn;
  status: ConnectionStatusType;
  isConnected: boolean;
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null);

interface WebSocketProviderProps {
  children: ReactNode;
}

export function WebSocketProvider({ children }: WebSocketProviderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId, playerId, playerToken, sessionRestorePending, setSessionError, clearSession } =
    useGameStore();
  const requestResyncRef = useRef<() => void>(() => {});

  // Stable callback that reads location.pathname at call time, not capture time
  const getLocationPathname = useCallback(() => location.pathname, [location.pathname]);

  const routeSessionId = useMemo(() => {
    const match = location.pathname.match(/^\/(?:lobby|game)\/([^/]+)/);
    return match?.[1] ?? null;
  }, [location.pathname]);

  const routeSessionMismatch = Boolean(routeSessionId && routeSessionId !== sessionId);
  const shouldConnect = Boolean(
    sessionId &&
      playerId &&
      playerToken &&
      !sessionRestorePending &&
      !routeSessionMismatch
  );

  // Centralized message handler — reads store state fresh on each message
  const handleCentralMessage = useMessageHandlers({
    navigate,
    getLocationPathname,
    requestResync: () => requestResyncRef.current(),
  });

  // Handle fatal WebSocket errors (session not found, player not found)
  const handleFatalError = useCallback(
    (code: number, reason: string) => {
      logger.error('websocket_fatal_error', { code, reason });
      setSessionError(`Connection failed: ${reason || 'Session not found'}`);
      clearSession();
    },
    [clearSession, setSessionError]
  );

  // WebSocket connection — managed at context level to persist across route changes
  const { sendMessage, status, requestResync } = useWebSocket({
    sessionId: shouldConnect ? sessionId || '' : '',
    playerId: shouldConnect ? playerId || '' : '',
    playerToken: shouldConnect ? playerToken || '' : '',
    onMessage: handleCentralMessage,
    onFatalError: handleFatalError,
  });

  useEffect(() => {
    requestResyncRef.current = requestResync;
  }, [requestResync]);

  // Listen for profile updates from Settings page to broadcast to current session
  useEffect(() => {
    const handleProfileUpdate = (
      event: CustomEvent<{
        profile_emoji?: string;
        theme_primary_color?: string;
      }>
    ) => {
      if (status === ConnectionStatus.CONNECTED && sessionId) {
        const { profile_emoji, theme_primary_color } = event.detail;
        sendMessage('UPDATE_PLAYER_PROFILE', {
          profile_emoji,
          theme_primary_color,
        });
      }
    };

    window.addEventListener('profile-updated', handleProfileUpdate as EventListener);
    return () => {
      window.removeEventListener('profile-updated', handleProfileUpdate as EventListener);
    };
  }, [status, sessionId, sendMessage]);

  const value = useMemo<WebSocketContextValue>(
    () => ({
      sendMessage,
      status,
      isConnected: status === ConnectionStatus.CONNECTED,
    }),
    [sendMessage, status]
  );

  // Expose sendMessage on window for E2E testing
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as unknown as Record<string, unknown>).__WS_SEND_MESSAGE__ = sendMessage;
    }
    return () => {
      if (typeof window !== 'undefined') {
        delete (window as unknown as Record<string, unknown>).__WS_SEND_MESSAGE__;
      }
    };
  }, [sendMessage]);

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useWebSocketContext(): WebSocketContextValue {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocketContext must be used within WebSocketProvider');
  }
  return context;
}

// Re-export ConnectionStatus for convenience
export { ConnectionStatus };
